import { createRouter, createWebHistory } from 'vue-router'

const routes = [
  // Root: smart redirect
  { path: '/', redirect: '/portal/splash' },

  // ── Azure AD SSO callback ────────────────────────────────────────────────
  {
    path: '/oauth2/done',
    name: 'OAuthCallback',
    component: () => import('@/views/OAuthCallback.vue'),
    meta: { requiresAuth: false, requiresPortalAuth: false },
  },

  // ── BI Report SSO bridge (already logged in to Portal, need assertion) ──
  {
    path: '/portal/bi-sso',
    name: 'BiSso',
    component: () => import('@/views/portal/BiSso.vue'),
    meta: { requiresPortalAuth: true },
  },

  // ── Portal (public-facing) ────────────────────────────────────────────────
  {
    path: '/portal/konebi-public-share',
    name: 'KoneBIPublicShare',
    component: () => import('@/views/portal/konebi-public-share/index.vue'),
    meta: { requiresPortalAuth: false },
  },
  {
    path: '/portal/konebi-share',
    name: 'KoneBIShare',
    component: () => import('@/views/portal/konebi-share/index.vue'),
    meta: { requiresPortalAuth: true },
  },
  {
    path: '/portal/splash',
    name: 'PortalSplash',
    component: () => import('@/views/portal/PortalSplash.vue'),
    meta: { requiresPortalAuth: false },
  },
  {
    path: '/portal/login',
    name: 'PortalLogin',
    component: () => import('@/views/portal/PortalLogin.vue'),
    meta: { requiresPortalAuth: false },
  },
  {
    path: '/portal',
    name: 'PortalLayout',
    component: () => import('@/views/portal/PortalLayout.vue'),
    meta: { requiresPortalAuth: true },
    children: [
      { path: '', redirect: '/portal/home' },
      {
        path: 'home',
        name: 'PortalHome',
        component: () => import('@/views/portal/PortalHome.vue'),
      },
      {
        path: 'deeptrace',
        name: 'DeepTrace',
        component: () => import('@/views/portal/deeptrace/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      {
        path: 'sitesense',
        name: 'SiteSense',
        component: () => import('@/views/portal/sitesense/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      {
        path: 'konemarket',
        name: 'KoneMarket',
        component: () => import('@/views/portal/konemarket/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      {
        path: 'aireport',
        name: 'AIReport',
        component: () => import('@/views/portal/aireport/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      {
        path: 'bireport',
        name: 'BIReport',
        component: () => import('@/views/portal/bireport/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      {
        path: 'konebireport',
        name: 'KoneBIReport',
        component: () => import('@/views/portal/konebireport/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      {
        path: 'integrationdemo',
        name: 'IntegrationDemo',
        component: () => import('@/views/portal/integrationdemo/index.vue'),
        meta: { requiresPortalAuth: true },
      },
      // Catch-all under /portal — PortalPage resolves local vs remote
      {
        path: ':pathMatch(.*)*',
        name: 'PortalPage',
        component: () => import('@/views/portal/PortalPage.vue'),
      },
    ],
  },

  // ── Admin (back-office) ───────────────────────────────────────────────────
  {
    path: '/admin/login',
    name: 'Login',
    component: () => import('@/views/admin/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/admin/dashboard' },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
      },
      {
        path: 'users',
        name: 'UserManage',
        component: () => import('@/views/admin/UserManage.vue'),
      },
      {
        path: 'roles',
        name: 'RoleManage',
        component: () => import('@/views/admin/RoleManage.vue'),
      },
      {
        path: 'menus',
        name: 'MenuManage',
        component: () => import('@/views/admin/MenuManage.vue'),
      },
      {
        path: 'permissions',
        name: 'PermissionManage',
        component: () => import('@/views/admin/PermissionManage.vue'),
      },
      {
        path: 'sitesense',
        name: 'SiteSenseAdmin',
        component: () => import('@/views/admin/SiteSensePage.vue'),
      },
      {
        path: 'tenants',
        name: 'TenantManage',
        component: () => import('@/views/admin/TenantManage.vue'),
      },
      {
        path: 'llm/providers',
        name: 'LlmProviderManage',
        component: () => import('@/views/admin/llm/ProviderManage.vue'),
      },
      {
        path: 'llm/workspaces',
        name: 'LlmWorkspaceManage',
        component: () => import('@/views/admin/llm/WorkspaceManage.vue'),
      },
      {
        path: 'llm/cost-report',
        name: 'LlmCostReport',
        component: () => import('@/views/admin/llm/CostReport.vue'),
      },
      {
        path: 'llm/alert-rules',
        name: 'LlmAlertRuleManage',
        component: () => import('@/views/admin/llm/AlertRuleManage.vue'),
      },
      {
        path: 'llm/alert-records',
        name: 'LlmAlertRecordManage',
        component: () => import('@/views/admin/llm/AlertRecordManage.vue'),
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to, from, next) => {
  const adminToken = localStorage.getItem('access_token')
  const portalToken = localStorage.getItem('portal_token')
  const userRoles = JSON.parse(localStorage.getItem('user_roles') || '[]')

  // Portal guard
  if (to.meta.requiresPortalAuth !== false && to.path.startsWith('/portal')) {
    if (!portalToken) {
      // Carry the originally-requested Portal path through login so the user
      // lands back on it (e.g. /portal/konebi-share?id=xxx) instead of home.
      return next({ name: 'PortalLogin', query: { redirect: to.fullPath } })
    }
  }
  // Redirect authenticated users away from splash/login
  if ((to.name === 'PortalSplash' || to.name === 'PortalLogin') && portalToken) {
    const redirectTarget = to.query.redirect || to.query.next
    if (redirectTarget) {
      // Internal Portal path (e.g. from the guard above) — navigate directly.
      if (String(redirectTarget).startsWith('/')) {
        return next(redirectTarget)
      }
      // External subsystem URL (e.g. a BI Report share link) — hand off to the
      // SSO bridge page which fetches the assertion and redirects out of Portal.
      return next({ name: 'BiSso', query: { redirect: redirectTarget } })
    }
    return next({ name: 'PortalHome' })
  }

  // Admin guard
  const requiresAdmin = to.meta.requiresAuth !== false && to.path.startsWith('/admin')
  if (requiresAdmin) {
    if (!adminToken) {
      return next({ name: 'Login' })
    }
    if (!userRoles.includes('superadmin')) {
      // Token exists but user is not superadmin — redirect back to login
      return next({ name: 'Login' })
    }
  }
  if (to.name === 'Login' && adminToken && userRoles.includes('superadmin')) {
    return next({ name: 'Dashboard' })
  }

  next()
})

export default router

